import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Input, Card, Row, Col, Select, Switch, message, Modal, Spin } from "antd";
import { Api_Url } from "../../../config/config";
import moment from "moment";
import axios from "axios";
import { BranchModalAdd } from "./BranchModalAdd";
import { BranchModalUpdate } from "./BranchModalUpdate";
import { ErrorToast } from "../../ToastComponent";

const { Option } = Select;

export const BranchMaster = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  // Ids currently being toggled (to show a loading spinner on that row's switch)
  const [togglingId, setTogglingId] = useState(null);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    region: "",
    searchTerm: "",
  });

  // --- Check Branch by Name ---
  const [isCheckNameModalOpen, setIsCheckNameModalOpen] = useState(false);
  const [checkNameInput, setCheckNameInput] = useState("");
  const [checkNameResults, setCheckNameResults] = useState([]);
  const [checkNameLoading, setCheckNameLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const checkBranchByName = async () => {
    const names = checkNameInput
      .split(/[\n,]+/)
      .map((n) => n.trim())
      .filter((n) => n.length > 0);

    if (names.length === 0) return;

    setCheckNameLoading(true);
    setCheckNameResults([]);

    const token = JSON.parse(sessionStorage.getItem("user"))?.accessToken;

    const results = await Promise.all(
      names.map(async (name) => {
        try {
          const response = await axios.get(
            `${Api_Url}api/v1/Branch/byname/${encodeURIComponent(name)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const records = response?.data?.data;
          const found = Array.isArray(records) && records.length > 0;
          return { name, found, matches: found ? records : [] };
        } catch {
          return { name, found: false, matches: [] };
        }
      })
    );

    setCheckNameResults(results);
    setCheckNameLoading(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${Api_Url}api/v1/Branch/all`, {
        headers: {
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user"))?.accessToken
          }`,
        },
      });
      setData(response?.data?.data || []);
    } catch (error) {
      ErrorToast("Failed to fetch branches");
    } finally {
      setLoading(false);
    }
  };

  // Enable/disable a branch by flipping IsActive via the update endpoint.
  const handleToggleActive = async (record, checked) => {
    setTogglingId(record.id);
    try {
      const payload = {
        Id: record.id,
        Name: record.name,
        Code: record.code ?? null,
        AddressId: record.addressId ?? null,
        Region: record.region ?? null,
        Cluster: record.cluster ?? null,
        Zone: record.zone ?? null,
        OpeningDate: record.openingDate
          ? moment(record.openingDate).format("YYYY-MM-DD")
          : null,
        ClosingDate: record.closingDate
          ? moment(record.closingDate).format("YYYY-MM-DD")
          : null,
        IsActive: checked,
      };

      const response = await axios.put(
        `${Api_Url}api/v1/Branch/${record.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        message.success(
          `Branch ${checked ? "enabled" : "disabled"} successfully`
        );
        // Optimistically update the row without a full refetch.
        setData((prev) =>
          prev.map((b) =>
            b.id === record.id ? { ...b, isActive: checked } : b
          )
        );
      }
    } catch (error) {
      message.error(
        error.response?.data?.message || "Failed to update branch status"
      );
    } finally {
      setTogglingId(null);
    }
  };

  // Distinct regions for the filter dropdown
  const regions = useMemo(() => {
    return [...new Set(data.map((item) => item.region).filter(Boolean))];
  }, [data]);

  const filteredData = useMemo(() => {
    let filtered = [...data];
    if (filters.region) {
      filtered = filtered.filter(
        (item) => item.region?.toLowerCase() === filters.region.toLowerCase()
      );
    }
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name?.toLowerCase().includes(term) ||
          item.code?.toLowerCase().includes(term) ||
          item.cluster?.toLowerCase().includes(term) ||
          item.zone?.toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [data, filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ region: "", searchTerm: "" });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      sorter: (a, b) => a.id - b.id,
      width: 80,
    },
    {
      title: "Branch Name",
      dataIndex: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Code",
      dataIndex: "code",
      render: (val) => val || "NA",
    },
    {
      title: "Region",
      dataIndex: "region",
      render: (val) => val || "NA",
    },
    {
      title: "Cluster",
      dataIndex: "cluster",
      render: (val) => val || "NA",
    },
    {
      title: "Zone",
      dataIndex: "zone",
      render: (val) => val || "NA",
    },
    {
      title: "Opening Date",
      dataIndex: "openingDate",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "NA"),
    },
    {
      title: "Closing Date",
      dataIndex: "closingDate",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "NA"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 160,
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
      render: (isActive, record) => (
        <Switch
          checked={!!isActive}
          loading={togglingId === record.id}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Button
          type="primary"
          ghost
          onClick={() => {
            setSelectedBranch(record);
            setIsUpdateModalOpen(true);
          }}
        >
          Update
        </Button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Header Section */}
        <div className="row mb-3">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Branches</h3>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary"
                onClick={() => setIsCheckNameModalOpen(true)}
              >
                <i className="fa-solid fa-magnifying-glass me-2"></i> Check Branch by Name
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setIsAddModalOpen(true)}
              >
                <i className="fa-solid fa-plus me-2"></i> Add Branch
              </button>
            </div>
          </div>
        </div>

        {/* Filter Card */}
        <Card className="mb-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search by name, code, cluster, zone..."
                value={filters.searchTerm}
                onChange={(e) =>
                  handleFilterChange("searchTerm", e.target.value)
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Filter by Region"
                value={filters.region || undefined}
                onChange={(value) => handleFilterChange("region", value)}
                allowClear
                showSearch
                style={{ width: "100%" }}
              >
                {regions.map((region) => (
                  <Option key={region} value={region}>
                    {region}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Button onClick={clearFilters} block>
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Table Section */}
        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            loading={loading}
            pagination={{
              showSizeChanger: true,
              defaultPageSize: 10,
              pageSizeOptions: ["10", "20", "50", "100", "200"],
            }}
            scroll={{ x: 1300 }}
          />
        </div>

        {/* Modals */}
        {isAddModalOpen && (
          <BranchModalAdd
            onClose={() => setIsAddModalOpen(false)}
            onSuccess={() => {
              setIsAddModalOpen(false);
              fetchData();
            }}
          />
        )}

        {isUpdateModalOpen && (
          <BranchModalUpdate
            branch={selectedBranch}
            onClose={() => setIsUpdateModalOpen(false)}
            onSuccess={() => {
              setIsUpdateModalOpen(false);
              fetchData();
            }}
          />
        )}

        {/* Check Branch by Name Modal */}
        <Modal
          title={
            <h5 className="mb-0">
              <i className="fa-solid fa-magnifying-glass me-2" />
              Check Branch by Name
            </h5>
          }
          open={isCheckNameModalOpen}
          onCancel={() => {
            setIsCheckNameModalOpen(false);
            setCheckNameInput("");
            setCheckNameResults([]);
          }}
          width={780}
          footer={null}
          maskClosable={false}
          closeIcon={<span style={{ color: "red" }}>✕</span>}
        >
          <div className="mb-3">
            <label className="form-label fw-semibold">
              Enter Branch Names&nbsp;
              <span className="text-muted fw-normal">
                (one per line, or comma-separated)
              </span>
            </label>
            <textarea
              className="form-control"
              rows={5}
              placeholder={"Noida Sector 62\nMumbai Central\nJaipur"}
              value={checkNameInput}
              onChange={(e) => setCheckNameInput(e.target.value)}
            />
          </div>

          <div className="d-flex gap-2 mb-4">
            <button
              className="btn btn-primary"
              onClick={checkBranchByName}
              disabled={checkNameLoading || !checkNameInput.trim()}
            >
              {checkNameLoading ? (
                <>
                  <Spin size="small" className="me-2" />
                  Checking…
                </>
              ) : (
                <>
                  <i className="fa-solid fa-magnifying-glass me-2" />
                  Check
                </>
              )}
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                setCheckNameInput("");
                setCheckNameResults([]);
              }}
            >
              Clear
            </button>
          </div>

          {checkNameResults.length > 0 && (() => {
            const foundCount = checkNameResults.filter((r) => r.found).length;
            const notFoundCount = checkNameResults.length - foundCount;
            const tableRows = checkNameResults.flatMap((r, i) =>
              r.found
                ? r.matches.map((m, j) => ({
                    key: `${i}-${j}`,
                    searchedName: r.name,
                    id: m.id,
                    name: m.name,
                    code: m.code || "NA",
                    region: m.region || "NA",
                    isActive: m.isActive,
                    found: true,
                  }))
                : [
                    {
                      key: `${i}-notfound`,
                      searchedName: r.name,
                      id: null,
                      name: "—",
                      code: "—",
                      region: "—",
                      isActive: null,
                      found: false,
                    },
                  ]
            );

            return (
              <>
                <div className="d-flex gap-3 mb-3">
                  <span className="badge bg-success fs-6">
                    Found: {foundCount}
                  </span>
                  <span className="badge bg-danger fs-6">
                    Not Found: {notFoundCount}
                  </span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table className="table table-bordered table-sm">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Searched Name</th>
                        <th>Branch ID</th>
                        <th>Branch Name</th>
                        <th>Code</th>
                        <th>Region</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((row, idx) => (
                        <tr key={row.key}>
                          <td>{idx + 1}</td>
                          <td>
                            <strong>{row.searchedName}</strong>
                          </td>
                          <td>{row.id ?? "—"}</td>
                          <td>{row.name}</td>
                          <td>{row.code}</td>
                          <td>{row.region}</td>
                          <td>
                            {row.found ? (
                              row.isActive ? (
                                <span className="badge bg-success">✓ Active</span>
                              ) : (
                                <span className="badge bg-warning text-dark">Inactive</span>
                              )
                            ) : (
                              <span className="badge bg-danger">✗ Not Found</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </Modal>
      </div>
    </div>
  );
};
